import { AppStoreSnapshot, InstallableAppFormSettings, AppVersionInfoWithTargetPlatforms } from '../../types';
import { InstallableAppSettings, InstallableAppPublishingSettings, SkillAccess } from '../../model/settings';
import { getParameterWithDefault } from './common';
import i18n from '@yandex-int/i18n';
import * as settingsErrorsKeyset from 'i18n/SettingsErrors';
import { FormErrors } from 'client/hocs/types';

const tErrors = i18n(settingsErrorsKeyset);

const initialValues: InstallableAppFormSettings = {
    activationPhrases: [],
    brandVerificationWebsite: '',
    logoId: '',
    developerName: '',
    description: '',
    noteForModerator: '',
    email: '',
    isTrusted: false,
    category: '',
    hideInStore: false,
    packageName: '',
    name: '',
    skillAccess: SkillAccess.Private,
    secondaryTitle: '',
    ageRestriction: undefined,
    apkInfoWithTargets: {
        appVersionId: undefined,
        url: undefined,
        versionNotes: undefined,
        versionCode: undefined,
        versionName: undefined,
    },
    screenshots: [],
    eulaAgreement: undefined,
    privacyAgreement: undefined,
};

export function mapValuesToInstallableAppSettings(
    values: InstallableAppFormSettings,
    appStoreSnapshot: AppStoreSnapshot,
): Partial<InstallableAppSettings> {
    return {
        activationPhrases: values.activationPhrases,
        logoId: values.logoId,
        packageName: values.packageName,
        noteForModerator: values.noteForModerator,
        name: values.name,
        ageRestriction: values.ageRestriction,
        apkInfoWithTargets:
            values.apkInfoWithTargets?.appVersionId !== undefined
                ? {
                      ...(values.apkInfoWithTargets as AppVersionInfoWithTargetPlatforms),
                  }
                : undefined,
        screenshots: values.screenshots,
        privacyAgreement: values.privacyAgreement,
        eulaAgreement: values.eulaAgreement,
        skillAccess: values.skillAccess,
        isTrusted: __appConfig.user.isAdmin ? values.isTrusted : undefined,

        publishingSettings: {
            email: values.email,
            description: values.description,
            developerName: values.developerName,
            category: values.category,
            secondaryTitle: values.secondaryTitle,
        },
    };
}

export function mapInstallableAppSettingsToValues(settings: InstallableAppSettings): InstallableAppFormSettings {
    const { description, category, developerName, secondaryTitle, email } = {
        ...initialValues,
        ...settings.publishingSettings,
    } as Required<InstallableAppPublishingSettings>;

    const activationPhrases =
        settings.activationPhrases && settings.activationPhrases.filter(Boolean).length > 0
            ? settings.activationPhrases
            : [settings.name];

    return {
        ...initialValues,

        activationPhrases,
        description,
        developerName,
        secondaryTitle,
        category,
        isTrusted: !!settings.isTrusted,
        email,
        packageName: getParameterWithDefault(settings, initialValues, 'packageName'),
        noteForModerator: getParameterWithDefault(settings, initialValues, 'noteForModerator'),
        name: getParameterWithDefault(settings, initialValues, 'name'),
        ageRestriction: getParameterWithDefault(settings, initialValues, 'ageRestriction'),
        // can return null from server
        apkInfoWithTargets:
            getParameterWithDefault(settings, initialValues, 'apkInfoWithTargets') || initialValues.apkInfoWithTargets,
        screenshots: getParameterWithDefault(settings, initialValues, 'screenshots'),
        privacyAgreement: getParameterWithDefault(settings, initialValues, 'privacyAgreement'),
        eulaAgreement: getParameterWithDefault(settings, initialValues, 'eulaAgreement'),
        skillAccess: getParameterWithDefault(settings, initialValues, 'skillAccess'),
    };
}

export const getStringFieldErrorMessage = <TFieldName extends keyof InstallableAppFormSettings>(
    fieldName: TFieldName,
    fieldValue: string,
    zeroLengthErrorMessage: string,
    maxLength: number,
    maxLengthErrorMessage: string,
): Partial<FormErrors<InstallableAppFormSettings>> => {
    if (!fieldValue?.length) return { [fieldName]: zeroLengthErrorMessage };
    if (maxLength && fieldValue.length > maxLength) return { [fieldName]: maxLengthErrorMessage };
    return { [fieldName]: '' };
};

// TODO: handle typed errors from backend. See PASKILLS-9594
export const mapApiErrorsToFormErrors = (message?: string): FormErrors<InstallableAppFormSettings> => {
    if (message === 'wrong package name format') return { packageName: tErrors('wrong-package-name') };
    if (message === 'package name is missing') return { packageName: tErrors('package-name-missing') };
    if (message === "name or activationPhrases 'Содержит общие фразы'")
        return { activationPhrases: [tErrors('too-common-phrases')] };
    if (message === 'TV App with the same name already exists') return { name: tErrors('already-exist') };
    if (message === 'app name is quoted') return { name: tErrors('name-quouted') };
    if (message === 'contains forbidden word') return { name: tErrors('forbidden-words') };
    if (message === 'Inflected words were used in a developer name')
        return { developerName: tErrors('forbidden-developer-name') };
    // TODO: move general error away from name field
    if (message === 'Unknown error') return { name: tErrors('unknown-error') };
    if (message) return { name: message };
    return {};
};
